import React, { useCallback, useEffect, useRef, useState } from 'react';
import BraintreeWebDropIn from "braintree-web-drop-in";
import {
    Alert,
    AlertDescription,
    AlertIcon,
    AlertTitle,
    Box,
    Center,
    Flex,
    Spinner,
    useStyleConfig
} from '@chakra-ui/react';
import { coreUrls } from "core_src/js/core_urls_config";
import axios from "axios";


/**
 * Display the Braintree web drop-in fields.
 *
 * Valid props:
 *  - environment: The environment to use for paypal. Options are 'sandbox' or 'production'.
 *  - clientToken: The braintree client token or tokenization key to use to authenticate with Braintree
 *  - onInitialization: Function to be called when the braintree drop-in api has been intialized. Gets passed the
 *      braintree api instance.
 *  - onChangeActiveView - Function to be called when the `changeActiveView` event is fired by braintree.
 *      Reference: https://braintree.github.io/braintree-web-drop-in/docs/current/Dropin.html#event:changeActiveView
 *  - onPaymentMethodRequestable - Function to be called when the `paymentMethodRequestable` event is fired by
 *      braintree. Reference: https://braintree.github.io/braintree-web-drop-in/docs/current/Dropin.html#event:paymentMethodRequestable
 *  - onNoPaymentMethodRequestable - Function to be called when the noPaymentMethodRequestable event is fired by
 *      braintree. Reference: https://braintree.github.io/braintree-web-drop-in/docs/current/Dropin.html#event:noPaymentMethodRequestable
 *  - onPaymentOptionSelected - Function to be called when the `paymentOptionSelected` event is fired by braintree.
 *      Reference: https://braintree.github.io/braintree-web-drop-in/docs/current/Dropin.html#event:paymentOptionSelected
 *  - paypalEnabled - Whether or not to show the Paypal button as a payment option.
 *  - requestShippingAddress - Whether or not to have paypal collect the users shipping information.
 */
export const BraintreeDropIn = (props) => {

    const {
        environment = 'production',
        clientToken,
        onInitialization,
        onChangeActiveView,
        onPaymentMethodRequestable,
        onNoPaymentMethodRequestable,
        onPaymentOptionSelected,
        paypalEnabled = true,
        requestShippingAddress = false,
        useCustomerID = true,
    } = props;

    const styles = useStyleConfig('BraintreeDropIn');

    const braintree = useRef();
    const subscribedEvents = useRef([]);

    const [isLoadingBraintree, setIsLoadingBraintree] = useState(true);
    const [hasError, setHasError] = useState(false);

    const initializeBraintree = useCallback(async (wrapper) => {
        // This gets called with a null wrapper when the element is destroyed.
        if (!wrapper) return;

        let authorization = clientToken;
        let braintreeCustomerId;
        // If no client token provided, then request a client token from Anvil
        if (!authorization) {
            let response;
            try {
                response = await axios.post(coreUrls.braintree_client_token, {use_customer_id: useCustomerID});
            } catch (e) {
                setIsLoadingBraintree(false);
                setHasError(true);
                console.error('Error requesting braintree client token', e);
                return;
            }
            const {
                client_token: clientTokenApi,
                customer_id: customerId = null,
            } = response.data;
            authorization = clientTokenApi;
            braintreeCustomerId = customerId;
        }

        const braintreeConfig = {
            authorization: authorization,
            container: wrapper,
        }

        if (paypalEnabled) {
            braintreeConfig.paypal = {
                flow: 'vault',
                commit: false,
                env: environment,
                enableShippingAddress: requestShippingAddress,
                buttonStyle: {
                    size: 'responsive',
                }
            }
        }

        let braintree_instance;
        try {
            braintree_instance = await BraintreeWebDropIn.create(braintreeConfig);
        } catch (e) {
            setIsLoadingBraintree(false);
            setHasError(true);
            console.error('Error initializing braintree', e);
            return;
        }

        setIsLoadingBraintree(false);
        braintree.current = braintree_instance;

        if (onInitialization) {
            onInitialization({
                dropInInstance: braintree_instance,
                customerId: braintreeCustomerId,
            });
        }

        if (onNoPaymentMethodRequestable) {
            braintree_instance.on('noPaymentMethodRequestable', onNoPaymentMethodRequestable);
            subscribedEvents.current.push(['noPaymentMethodRequestable', onNoPaymentMethodRequestable]);
        }
        if (onChangeActiveView) {
            braintree_instance.on('changeActiveView', onChangeActiveView);
            subscribedEvents.current.push(['changeActiveView', onChangeActiveView]);
        }
        if (onPaymentMethodRequestable) {
            braintree_instance.on('paymentMethodRequestable', onPaymentMethodRequestable);
            subscribedEvents.current.push(['paymentMethodRequestable', onPaymentMethodRequestable]);
        }
        if (onPaymentOptionSelected) {
            braintree_instance.on('paymentOptionSelected', onPaymentOptionSelected);
            subscribedEvents.current.push(['paymentOptionSelected', onPaymentOptionSelected]);
        }
    }, [
        clientToken,
        paypalEnabled,
        onInitialization,
        onNoPaymentMethodRequestable,
        onChangeActiveView,
        onPaymentMethodRequestable,
        onPaymentOptionSelected,
        environment,
        requestShippingAddress
    ]);

    // Teardown the braintree instance and all it's event listeners when the component gets unmounted.
    useEffect(() => {
        let currentSubscribedEvents = subscribedEvents.current;

        return () => {
            if (!braintree.current) return;

            currentSubscribedEvents.map(([eventName, handler]) => {
                braintree.current.off(eventName, handler)
            });

            braintree.current.teardown();
        }
    }, []);

    return (
        <>
            {isLoadingBraintree && !hasError && (
                <Center data-testid="loading-container">
                    <Spinner colorScheme="green"/>
                </Center>
            )}
            {hasError && (
                <Alert size="md" status="error" variant="subtle">
                    <AlertIcon/>
                    <Flex direction="column">
                        <AlertTitle>Unable to load payment information.</AlertTitle>
                        <AlertDescription>Please refresh the page to try again.</AlertDescription>
                    </Flex>
                </Alert>
            )}
            <Box display={isLoadingBraintree ? 'none' : 'initial'} __css={styles} ref={initializeBraintree}/>
        </>
    )
};
